// src/contexts/RoomContext.tsx
import type { ReactNode } from "react";
import { createContext, useContext, useCallback } from "react";
import type { MappedTuple } from "@/interfaces/estimate-detail";
import type { Category } from "@/components/estimation-details/Rooms/Lines";
import type { DuplicateCheckResult } from "@/components/estimation-details/Rooms/hooks/useLineItemDuplicateCheck";
import { useLineItemDuplicateCheck } from "@/components/estimation-details/Rooms/hooks/useLineItemDuplicateCheck";

interface RoomContextType {
  filterLines: MappedTuple[];
  itemCategoryTab: Record<Category, MappedTuple[]>;
  duplicateResult: DuplicateCheckResult;
  duplicateError: string;
  handleDuplicateCheck: (newLineItem: string, itemIdToIgnore?: string) => DuplicateCheckResult;
  clearError: () => void;
  onRemoveRecommendation: (item: MappedTuple) => void;
}

const RoomContext = createContext<RoomContextType | null>(null);

interface RoomProviderProps {
  children: ReactNode;
  filterLines: MappedTuple[];
  deleteLineItem: (tupleId: string, uniqueId: string, category?: Category) => Promise<void>;
  itemCategoryTab: Record<Category, MappedTuple[]>;
}

export const RoomProvider = ({ children, filterLines, itemCategoryTab, deleteLineItem }: RoomProviderProps) => {
  const { duplicateResult, duplicateError, handleDuplicateCheck, clearError } = useLineItemDuplicateCheck({
    filterLines,
    itemCategoryTab,
  });

  const onRemoveRecommendation = useCallback(
    async (item: MappedTuple) => {
      await deleteLineItem(item.tuple.tupleId, item.currentItem.uniqueId, "recommended");
    },
    [deleteLineItem],
  );

  return (
    <RoomContext.Provider
      value={{
        filterLines,
        itemCategoryTab,
        duplicateResult,
        duplicateError,
        handleDuplicateCheck,
        clearError,
        onRemoveRecommendation,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRoomContext = () => {
  const context = useContext(RoomContext);
  if (!context) {
    throw new Error("useRoomContext must be used within a RoomProvider");
  }
  return context;
};
